import { EventType } from "@/analytics/analytics-events";
import { useFileUploaderWithPromise } from "@/components/common/file-upload-context/use-file-uploader";
import { UploadElementType } from "@/components/common/point-cloud-file-upload-context/use-upload-element-types";
import { useCurrentProjectApiClient } from "@/components/common/project-provider/project-loading-context";
import { updateProject } from "@/components/common/project-provider/update-project";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useCurrentArea } from "@/modes/mode-data-context";
import {
  PointCloudAnalysis,
  setAnalysisDirtyFlag,
} from "@/store/point-cloud-analysis-tool-slice";
import { selectAnnotationSection } from "@/store/selections-selectors";
import { useAppDispatch, useAppStore } from "@/store/store-hooks";
import { setShowSpinner } from "@/store/ui/ui-slice";
import { createAnalysisJsonFile } from "@/tools/analysis-saving-utils";
import { selectIElementWorldMatrix4 } from "@/utils/transform-conversion-parsed";
import {
  selectChildDepthFirst,
  selectIElement,
  useToast,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import {
  isAnalysisSection,
  isAnnotationGroup,
} from "@faro-lotv/ielement-types";
import {
  createMutationAddAnalysis,
  createMutationExchangeFileUri,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useCallback } from "react";

/**
 * @param analysis analysis to save
 * @returns a function to save analysis
 */
export function useSaveAnalysisAction(
  analysis: PointCloudAnalysis,
): () => Promise<void> {
  const appStore = useAppStore();
  const client = useCurrentProjectApiClient();
  const { openToast } = useToast();
  const { handleErrorWithToast } = useErrorHandlers();
  const { area } = useCurrentArea();
  const { coreApiClient } = useApiClientContext();
  const uploadFile = useFileUploaderWithPromise();
  const dispatch = useAppDispatch();

  return useCallback(async () => {
    Analytics.track(EventType.saveColoredAnalysis);

    dispatch(setShowSpinner(true));

    const cloudElement = selectIElement(analysis.parentId)(appStore.getState());
    assert(
      cloudElement,
      "There must be a point cloud associated to the point cloud analysis",
    );
    const annotationSection = selectAnnotationSection(
      cloudElement,
      area,
    )(appStore.getState());
    assert(annotationSection, "There must be a parent annotation section");

    const worldMatrix = selectIElementWorldMatrix4(annotationSection.id)(
      appStore.getState(),
    );

    const file = createAnalysisJsonFile(analysis, worldMatrix);
    try {
      const { downloadUrl, md5 } = await uploadFile({
        file,
        coreApiClient,
        uploadElementType: UploadElementType.none,
        projectId: client.projectId,
        silent: true,
      });

      const analysisElement = selectIElement(analysis.id)(appStore.getState());
      if (analysisElement) {
        // IElement already exist, update file uri
        await client.applyMutations([
          createMutationExchangeFileUri(analysisElement.id, {
            md5Hash: md5,
            fileName: file.name,
            fileSize: file.size,
            uri: downloadUrl,
          }),
        ]);

        // Fetch the changed element and update the local copy of the project
        dispatch(
          updateProject({
            projectApi: client,
            iElementQuery: {
              ids: [analysisElement.id],
            },
          }),
        );
      } else {
        // add new IElementAnalysis

        // Check if the parent annotation section already contains an annotation group (Nodes group)
        const nodesGroup = selectChildDepthFirst(
          annotationSection,
          isAnnotationGroup,
          1,
        )(appStore.getState());

        // check if the group (Nodes) already contains analysis section
        const analysisSection = nodesGroup
          ? selectChildDepthFirst(
              nodesGroup,
              isAnalysisSection,
              1,
            )(appStore.getState())
          : undefined;

        await client.applyMutations([
          createMutationAddAnalysis({
            rootId: annotationSection.rootId,
            sectionId: annotationSection.id,
            newElementId: analysis.id,
            groupId: nodesGroup?.id,
            analysisSectionId: analysisSection?.id,
            signedUri: downloadUrl,
            md5Hash: md5,
            fileName: file.name,
            fileSize: file.size,
            name: "analysis",
          }),
        ]);

        // Fetch the changed section sub-tree and update the local copy of the project
        dispatch(
          updateProject({
            projectApi: client,
            iElementQuery: {
              ancestorIds: [annotationSection.id],
            },
          }),
        );
      }
    } catch (error) {
      handleErrorWithToast({
        title: "Failed to save point cloud analysis",
        error,
      });
      dispatch(setShowSpinner(false));
      return;
    }

    openToast({
      title: "Point cloud analysis has been saved",
      variant: "success",
    });

    dispatch(
      setAnalysisDirtyFlag({
        analysisId: analysis.id,
        dirty: false,
      }),
    );

    dispatch(setShowSpinner(false));
  }, [
    analysis,
    appStore,
    area,
    client,
    coreApiClient,
    dispatch,
    handleErrorWithToast,
    openToast,
    uploadFile,
  ]);
}
